import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthLayout } from '@/components/auth-layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/password-input';
import { authClient } from '@/lib/auth';

export const Route = createFileRoute('/reset-password')({
  validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }),
  component: ResetPasswordPage,
});

const NewPasswordSchema = z
  .object({
    password: z.string().min(8, 'Use at least 8 characters').max(128),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

function ResetPasswordPage() {
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof NewPasswordSchema>>({
    resolver: zodResolver(NewPasswordSchema),
    defaultValues: { password: '', confirm: '' },
  });

  if (!token || linkError) {
    return (
      <AuthLayout title="Link not valid" description="This link has expired or was already used.">
        <p className="text-center text-sm text-muted-foreground">
          Ask for a new one from the sign-in page (“Forgot password?”), or ask an admin to resend
          your invitation.
        </p>
        <Button asChild variant="outline" className="mt-6 w-full">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  async function onSubmit(values: z.infer<typeof NewPasswordSchema>) {
    setError(null);
    const { error: err } = await authClient.resetPassword({ newPassword: values.password, token });
    if (err) {
      setError(
        err.code === 'INVALID_TOKEN'
          ? 'This link has expired or was already used. Ask for a new one.'
          : (err.message ?? 'The password could not be set.'),
      );
      return;
    }
    await navigate({ to: '/login', search: { notice: 'password-set' } });
  }

  return (
    <AuthLayout
      title="Set your password"
      description="Choose a password for the Atlas Control Panel"
    >
      {error && (
        <Alert variant="destructive" className="mb-4" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" autoFocus {...field} />
                </FormControl>
                <FormDescription>At least 8 characters. A short phrase works well.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : 'Set password'}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  );
}
